import * as THREE from 'three';
import { applyCarMaterials } from '@/entities/CarView';
import { trackLoad } from '@/core/loadTracker';
import { ModelLoader, modelLoader } from '@/render/ModelLoader';

/**
 * propModels.ts — M158: the hand-built course props (scripts/blender/props/,
 * built with the cars' own carlib2): the TAXI sedan shared by the parked NYC
 * taxis and the crossing cabs (NYC yellow, Tokyo black), and the Lagos DANFO
 * minibus. Paint is tinted through the cars' own material pass; small extras
 * (the roof sign, the luggage on the danfo's rack) are added here.
 *
 * The procedural build stays as the stand-in: callers hand in the group that
 * already holds it, and it is hidden the moment the model lands (it never
 * leaves an obstacle invisible). The load is registered with the load
 * tracker, so the race warm-up waits for it like the landmarks.
 */

export interface PropStyle {
  /** The model file in public/models/props (no extension). */
  model: string;
  /** Body paint (tinted through applyCarMaterials, like a ride's stock paint). */
  paint: number;
  /** Overall length in world units (kept inside the prop's collision shape). */
  length: number;
  /** In-game extras once the model is fitted; `top` = its highest point. */
  decorate?: (fitted: THREE.Group, top: number) => void;
}

let signTex: THREE.CanvasTexture | null = null;
function signTexture(): THREE.CanvasTexture {
  if (signTex) return signTex;
  const c = document.createElement('canvas');
  c.width = 256;
  c.height = 64;
  const g = c.getContext('2d');
  if (g) {
    g.fillStyle = '#ffffff';
    g.fillRect(0, 0, 256, 64);
    g.fillStyle = '#1a1a1a';
    g.font = 'bold 46px Arial, Helvetica, sans-serif';
    g.textAlign = 'center';
    g.textBaseline = 'middle';
    g.fillText('TAXI', 128, 35);
  }
  signTex = new THREE.CanvasTexture(c);
  signTex.colorSpace = THREE.SRGBColorSpace;
  return signTex;
}

/** A lit roof sign: a rounded box on a dark base; the long faces carry the
 *  TAXI lettering, glowing in the sign colour. Sits on the roof at `roofY`. */
function roofSign(color: number, roofY: number, z: number): THREE.Group {
  const g = new THREE.Group();
  const lettered = new THREE.MeshStandardMaterial({
    color: 0xffffff,
    map: signTexture(),
    emissive: color,
    emissiveMap: signTexture(),
    emissiveIntensity: 0.9,
    roughness: 0.35,
  });
  const plain = new THREE.MeshStandardMaterial({ color, emissive: color, emissiveIntensity: 0.6, roughness: 0.4 });
  const base = new THREE.Mesh(
    new THREE.BoxGeometry(0.78, 0.05, 0.32),
    new THREE.MeshStandardMaterial({ color: 0x15171c, roughness: 0.5 }),
  );
  base.position.y = 0.025;
  // Box face order: +x, -x, +y, -y, +z, -z → the long axis runs across the
  // car and the lettering faces front and back.
  const sign = new THREE.Mesh(new THREE.BoxGeometry(0.72, 0.2, 0.24), [
    plain,
    plain,
    plain,
    plain,
    lettered,
    lettered,
  ]);
  sign.position.y = 0.15;
  g.add(base, sign);
  g.position.set(0, roofY, z);
  return g;
}

/** A warm lit interior behind the windows (Tokyo's night cab, the bus decks). */
function litGlass(fitted: THREE.Group, intensity: number): void {
  fitted.traverse((o) => {
    const m = o as THREE.Mesh;
    if (!m.isMesh || !/^Glass/.test(m.name) || Array.isArray(m.material)) return;
    const lit = (m.material as THREE.MeshStandardMaterial).clone();
    lit.emissive = new THREE.Color(0xffd98c);
    lit.emissiveIntensity = intensity;
    m.material = lit;
  });
}

/** The taxi: a roof sign in `sign`, and optionally a warm lit cabin (Tokyo's
 *  night cab reads by it). */
function taxi(paint: number, sign: number, litCabin = false): PropStyle {
  return {
    model: 'taxi',
    paint,
    length: 3.95,
    decorate: (fitted, top) => {
      if (litCabin) litGlass(fitted, 0.16);
      fitted.add(roofSign(sign, top - 0.01, 0));
    },
  };
}

export const NYC_TAXI = taxi(0xf4b512, 0xfff1c4);
export const TOKYO_TAXI = taxi(0x15181f, 0x2ee6a8, true);

/** Market bundles strapped to the danfo's rack (deterministic sizes). */
function danfoLuggage(fitted: THREE.Group, top: number): void {
  const cols = [0x3f6c8e, 0x7c5a34, 0x5e7042, 0x8a3b2e];
  const s = fitted.children[0]?.scale.x ?? 1; // the model's fit scale
  cols.forEach((col, i) => {
    const w = (0.95 - (i % 2) * 0.22) * s * 1.0;
    const h = (0.26 + (i % 3) * 0.07) * s;
    const b = new THREE.Mesh(
      new THREE.BoxGeometry(w, h, 0.72 * s),
      new THREE.MeshStandardMaterial({ color: col, roughness: 0.9 }),
    );
    b.position.set(((i % 2) - 0.5) * 0.18, top + h / 2 - 0.02, (1.15 - i * 0.82) * s);
    b.rotation.y = ((i * 37) % 7) / 40 - 0.08;
    b.castShadow = true;
    fitted.add(b);
  });
}

export const DANFO: PropStyle = { model: 'danfo', paint: 0xe6b81a, length: 5.4, decorate: danfoLuggage };

let destTex: THREE.CanvasTexture | null = null;
function destTexture(): THREE.CanvasTexture {
  if (destTex) return destTex;
  const c = document.createElement('canvas');
  c.width = 512;
  c.height = 96;
  const g = c.getContext('2d');
  if (g) {
    g.fillStyle = '#000000';
    g.fillRect(0, 0, 512, 96);
    g.fillStyle = '#ffc21a';
    g.font = 'bold 52px Arial, Helvetica, sans-serif';
    g.textBaseline = 'middle';
    g.textAlign = 'left';
    g.fillText('23', 18, 52);
    g.textAlign = 'center';
    g.fillText('WESTMINSTER', 312, 52);
  }
  destTex = new THREE.CanvasTexture(c);
  destTex.colorSpace = THREE.SRGBColorSpace;
  return destTex;
}

/** The double-decker's lit destination boards, laid over the dark board
 *  housings the model carries front and back (Destboard_*). */
function busBoards(fitted: THREE.Group): void {
  litGlass(fitted, 0.1); // a warm glow on both decks, like the old painted bus
  fitted.updateWorldMatrix(true, true);
  const mat = new THREE.MeshStandardMaterial({
    color: 0x000000,
    emissive: 0xffffff,
    emissiveMap: destTexture(),
    emissiveIntensity: 0.85,
    roughness: 0.4,
  });
  const boards: THREE.Mesh[] = [];
  fitted.traverse((o) => {
    if ((o as THREE.Mesh).isMesh && /^Destboard/.test(o.name)) boards.push(o as THREE.Mesh);
  });
  const box = new THREE.Box3();
  const c = new THREE.Vector3();
  const size = new THREE.Vector3();
  for (const b of boards) {
    box.setFromObject(b);
    box.getCenter(c);
    box.getSize(size);
    const out = Math.sign(c.z) || 1;
    const p = new THREE.Mesh(new THREE.PlaneGeometry(size.x * 0.94, size.y * 0.8), mat);
    p.position.set(c.x, c.y, box[out > 0 ? 'max' : 'min'].z + out * 0.006);
    if (out < 0) p.rotation.y = Math.PI;
    fitted.add(p);
  }
}

export const BUS: PropStyle = { model: 'bus', paint: 0xc8102e, length: 7.6, decorate: busBoards };

let tubMat: THREE.MeshPhysicalMaterial | null = null;
/** The rickshaw's tub is glossy black, not body paint (the hood + cowl are). */
function blackTub(fitted: THREE.Group): void {
  tubMat ??= new THREE.MeshPhysicalMaterial({
    color: 0x16171b,
    roughness: 0.32,
    metalness: 0.2,
    clearcoat: 0.8,
    clearcoatRoughness: 0.12,
  });
  fitted.traverse((o) => {
    if ((o as THREE.Mesh).isMesh && /^Tub/.test(o.name)) (o as THREE.Mesh).material = tubMat as THREE.Material;
  });
}

export const RICKSHAW: PropStyle = { model: 'rickshaw', paint: 0xf2c020, length: 2.7, decorate: blackTub };

/** The Napoli Vespa, built to the stand-in's measurements so its rider and
 *  kickstand (kept on swap) still sit right. */
export const vespaStyle = (paint: number): PropStyle => ({ model: 'vespa', paint, length: 2.2 });

/** Corner wheel pivots (tyre + rim + barrel per corner), so a crossing cab's
 *  wheels roll. Rotation about a pivot's local X = the axle. */
function wheelPivots(fitted: THREE.Object3D): { mesh: THREE.Object3D; radius: number }[] {
  fitted.updateWorldMatrix(true, true);
  const groups = new Map<string, { meshes: THREE.Mesh[]; tyre: THREE.Mesh | null }>();
  fitted.traverse((o) => {
    const m = o as THREE.Mesh;
    if (!m.isMesh) return;
    const hit = /^(Tyre|Rim|Trim_barrel|Chrome_disc|Trim_caliper)_([A-Z]{2})/.exec(m.name);
    if (!hit || hit[1] === 'Trim_caliper') return; // calipers stay put
    let e = groups.get(hit[2]);
    if (!e) groups.set(hit[2], (e = { meshes: [], tyre: null }));
    e.meshes.push(m);
    if (hit[1] === 'Tyre') e.tyre = m;
  });
  const out: { mesh: THREE.Object3D; radius: number }[] = [];
  const box = new THREE.Box3();
  const c = new THREE.Vector3();
  const size = new THREE.Vector3();
  const inv = new THREE.Matrix4();
  for (const { meshes, tyre } of groups.values()) {
    const anchor = tyre ?? meshes[0];
    const parent = anchor.parent;
    if (!parent) continue;
    box.setFromObject(anchor);
    box.getCenter(c);
    box.getSize(size);
    parent.updateWorldMatrix(true, false);
    inv.copy(parent.matrixWorld).invert();
    const pivot = new THREE.Group();
    pivot.position.copy(c).applyMatrix4(inv);
    parent.add(pivot);
    for (const m of meshes) pivot.attach(m);
    out.push({ mesh: pivot, radius: size.y / 2 });
  }
  return out;
}

/**
 * Swap a prop model into `root` once it loads: every child already in
 * `root` (the procedural stand-in) is hidden except those flagged
 * `userData.keepOnSwap` (contact shadows). `onWheels` receives the model's
 * wheel pivots (crossing cabs re-point their spin at them).
 */
export function attachPropModel(
  root: THREE.Group,
  style: PropStyle,
  onWheels?: (wheels: { mesh: THREE.Object3D; radius: number }[]) => void,
): void {
  // The props lab's `&before=1` shows the old stand-ins for side-by-side shots.
  if ((globalThis as { __propsLabBefore?: boolean }).__propsLabBefore) return;
  trackLoad(modelLoader.load(`${import.meta.env.BASE_URL}models/props/${style.model}.glb`))
    .then((model) => {
      const fitted = ModelLoader.fit(model, style.length, false);
      applyCarMaterials(fitted, style.paint);
      style.decorate?.(fitted, new THREE.Box3().setFromObject(fitted).max.y);
      for (const child of root.children) if (!child.userData.keepOnSwap) child.visible = false;
      root.add(fitted);
      onWheels?.(wheelPivots(fitted));
    })
    .catch((e: unknown) => console.warn(`[props] ${style.model} failed to load; keeping the stand-in`, e));
}
